import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, type Me } from '../lib/api';

export default function Dashboard({ me }: { me: Me }) {
  const [scenarios, setScenarios] = useState<any[] | null>(null);
  const [runs, setRuns] = useState<any[] | null>(null);
  const [mcqBest, setMcqBest] = useState<{ passed: boolean; score_pct: number } | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    api.scenarios()
      .then((r) => setScenarios(r.scenarios))
      .catch((e) => setErr(e.message || 'Failed to load scenarios'));
    api.myRuns().then((r) => setRuns(r.runs)).catch(() => setRuns([]));
    api.mcqHistory().then((r) => {
      const sessions = r.sessions || [];
      if (sessions.length === 0) { setMcqBest({ passed: false, score_pct: 0 }); return; }
      const best = sessions.reduce((b, s) => s.score_pct > b.score_pct ? s : b);
      setMcqBest({ passed: best.passed, score_pct: best.score_pct });
    }).catch(() => setMcqBest({ passed: false, score_pct: 0 }));
  }, []);

  const hasRuns = (runs?.length ?? 0) > 0;
  // Hard gate for examination/black-belt scenarios — RCUK requires 75% MCQ pass.
  const examGateOpen = mcqBest?.passed === true;
  const standardRunsPassed = (runs || []).filter((r) => r.outcome === 'ROSC' && r.difficulty === 'standard').length;
  const examGateMessage = !examGateOpen
    ? 'Pass the pre-course MCQ (75%) to unlock examination scenarios.'
    : standardRunsPassed < 3
    ? `Complete ${3 - standardRunsPassed} more successful Standard-difficulty run${3 - standardRunsPassed > 1 ? 's' : ''} to unlock Examination.`
    : '';
  const examFullyUnlocked = examGateOpen && standardRunsPassed >= 3;

  return (
    <div className="max-w-5xl mx-auto px-4 py-10">
      <div className="flex justify-between items-end mb-6">
        <div>
          <h1 className="text-2xl font-semibold">Welcome back, {me.display_name}</h1>
          <p className="text-mono-white/60 text-sm mt-1">Region: <span className="font-mono text-mono-white">{me.region_default}</span></p>
        </div>
      </div>

      {/* Pre-course study tools */}
      <div className="grid md:grid-cols-2 gap-3 mb-6">
        <Link to="/mcq" className="panel p-4 hover:border-brand-600 transition-colors block">
          <div className="flex justify-between items-start gap-3">
            <div>
              <h3 className="font-semibold">Pre-course MCQ</h3>
              <p className="text-xs text-mono-white/60 mt-1">10 randomised RCUK-grounded questions. 75% to pass.</p>
            </div>
            <span className="text-2xl">📝</span>
          </div>
        </Link>
        <Link to="/flashcards" className="panel p-4 hover:border-brand-600 transition-colors block">
          <div className="flex justify-between items-start gap-3">
            <div>
              <h3 className="font-semibold">Rhythm flashcards</h3>
              <p className="text-xs text-mono-white/60 mt-1">Train rhythm recognition against live-rendered strips.</p>
            </div>
            <span className="text-2xl">🫀</span>
          </div>
        </Link>
      </div>

      {/* First-time onboarding nudge */}
      {!hasRuns && runs != null && (
        <Link to="/onboarding" className="panel p-5 mb-6 bg-brand-600/10 border-l-4 border-brand-600 block hover:bg-brand-600/15 transition-colors">
          <div className="flex justify-between items-center gap-3 flex-wrap">
            <div>
              <h3 className="font-semibold text-brand-600">First time here? Take the 10-minute tour</h3>
              <p className="text-sm text-mono-white/70 mt-1">Walk through the monitor, rhythms, CPR, defib, drugs, and the 4Hs/4Ts before your first scenario.</p>
            </div>
            <span className="btn-primary text-sm shrink-0">Start tour →</span>
          </div>
        </Link>
      )}

      <h2 className="text-mono-white/60 uppercase tracking-widest text-xs mb-3">Scenarios</h2>
      {err && <p className="text-alarm-red text-sm">{err}</p>}
      {scenarios == null && !err && <p className="text-mono-white/60 text-sm">Loading…</p>}
      {scenarios && scenarios.length === 0 && (
        <div className="panel p-6 text-center text-mono-white/60">
          <p>No scenarios published yet.</p>
          <p className="text-xs mt-2">Scenarios are authored via Claude Code sessions on the server and reviewed by an instructor before publishing.</p>
        </div>
      )}
      {scenarios && scenarios.length > 0 && (
        <div className="grid md:grid-cols-2 gap-4">
          {scenarios.map((s) => {
            const isExam = s.difficulty === 'examination' || s.difficulty === 'black_belt';
            const locked = isExam && !examFullyUnlocked;
            const Wrapper: any = locked ? 'div' : Link;
            return (
              <Wrapper
                to={locked ? undefined : `/scenario/${s.id}`}
                key={s.id}
                className={`panel p-5 block ${locked ? 'opacity-60 cursor-not-allowed' : 'hover:border-brand-600 transition-colors'}`}
                title={locked ? examGateMessage : undefined}
              >
                <div className="flex justify-between items-start">
                  <h3 className="font-semibold">{s.title}</h3>
                  <span className={`text-xs px-2 py-0.5 rounded uppercase ${isExam ? 'bg-alarm-amber/20 text-alarm-amber' : 'bg-ink-800 text-mono-white/60'}`}>
                    {locked && '🔒 '}{s.difficulty}
                  </span>
                </div>
                <p className="text-xs text-mono-white/50 mt-2 italic">Hidden diagnosis hidden until run.</p>
                <div className="flex justify-between items-center mt-3">
                  <p className="text-xs text-mono-white/40">{s.status === 'published' ? 'Published' : 'Draft'}</p>
                  {locked && (
                    <Link to="/mcq" className="text-xs text-brand-600 hover:underline">{examGateMessage} →</Link>
                  )}
                </div>
              </Wrapper>
            );
          })}
        </div>
      )}

      {/* Recent runs */}
      {runs && runs.length > 0 && (
        <>
          <h2 className="text-mono-white/60 uppercase tracking-widest text-xs mt-8 mb-3">Your recent runs</h2>
          <div className="space-y-2">
            {runs.slice(0, 6).map((r) => (
              <Link key={r.id} to={`/debrief/${r.id}`} className="panel p-3 flex justify-between items-center hover:border-brand-600 transition-colors">
                <div>
                  <div className="text-sm font-medium">{r.scenario_title}</div>
                  <div className="text-xs text-mono-white/50 mt-1">
                    {new Date(r.started_at).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })} · {r.difficulty}
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  {r.outcome && <span className={`chip ${r.outcome === 'ROSC' ? 'bg-mono-green/20 text-mono-green' : r.outcome === 'death' ? 'bg-alarm-red/20 text-alarm-red' : 'bg-ink-800 text-mono-white/60'} uppercase text-[10px] tracking-widest`}>{r.outcome}</span>}
                  {r.score != null && <span className="font-mono text-sm">{r.score}/100</span>}
                </div>
              </Link>
            ))}
          </div>
          <div className="mt-4">
            <Link to="/onboarding" className="text-xs text-mono-white/40 hover:text-mono-white">Need a refresher? Re-take the familiarisation tour →</Link>
          </div>
        </>
      )}
    </div>
  );
}
